import { Check, X } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { type Cell, COMPARISON, MONTHS_BILLED_YEARLY, PLANS, TRIAL, TRIAL_PLAN } from '@/modules/marketing/pricing-data';
import { liveCells, useLivePricing } from '@/modules/marketing/use-live-pricing';

/**
 * The plans, the prices and what each one includes — as one table.
 *
 * ## Why one table and not cards above a table
 *
 * Cards above a comparison table meant reading the same plans twice, with the buttons off screen by
 * the time the detail was on it. Here the price and the button are the first row, and every row
 * after them is read against a button that is still a glance away.
 *
 * ## The free trial is a column, like the plans
 *
 * It is what every sign-up starts on, so it sits first and is compared row by row with what the
 * plans include: one server, three monitors, 25 Command Runner runs, no team — and everything else
 * ticked. A sentence above the table said the same thing less clearly and looked like an
 * afterthought.
 *
 * ## The numbers come from the catalogue
 *
 * The prices and allowances written in `pricing-data.ts` are what the pre-rendered HTML carries, so
 * a crawler and the first paint see something true. The catalogue then replaces them, which is what
 * makes a limit raised in the admin catalogue appear here without a deploy. See `use-live-pricing`.
 *
 * ## Lifted out of the landing page
 *
 * Because `/pricing` is a page of its own. Pricing is one of the two or three things somebody
 * searches for by name, and a section of the home page cannot be a search result, cannot be linked
 * to from an invoice, and cannot be given its own description. Two copies of a price table is not
 * a thing to have, so there is one component and both pages render it.
 */
/**
 * The label column's classes below the width the whole table fits in.
 *
 * Under 1024px the table scrolls sideways inside its card, and with five columns a phone shows one
 * plan at a time. Pinned, the label stays beside whichever plan is in view, so "25" is still read as
 * Command Runner runs rather than as a number with nothing beside it. The background has to be
 * opaque for the plans to pass underneath, and the border marks where they do.
 */
const PINNED = 'max-lg:sticky max-lg:left-0 max-lg:z-10 max-lg:border-r max-lg:border-border/60 max-lg:bg-card';

export function PricingTable() {
  const [yearly, setYearly] = useState(false);
  const live = useLivePricing();
  const plans = live?.plans ?? PLANS;
  const trialDays = live?.trialDays ?? TRIAL.days;

  /**
   * A plan's price for the period being shown, in whole currency units.
   *
   * A catalogue may price a year independently of twelve months; where it has, that price is the
   * one shown, because it is the one the checkout charges.
   */
  const priceOf = (plan: (typeof plans)[number]): number => {
    if (!yearly) {
      return plan.monthly;
    }

    const own = plan.yearly;

    return own === null || own === undefined ? plan.monthly * MONTHS_BILLED_YEARLY : own / 100;
  };

  return (
    <>
      {/* Monthly and yearly on one control rather than two tables. Two tables means two
          places to edit a price and one of them eventually gets missed. */}
      <div className="mt-10 flex justify-center">
        <div
          className="border-border/60 bg-muted/10 inline-flex rounded-full border p-1"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            onClick={() => setYearly(false)}
            aria-pressed={!yearly}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              yearly
                ? 'text-muted-foreground hover:text-foreground'
                : 'bg-primary text-primary-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            aria-pressed={yearly}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              yearly
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
            <span className={yearly ? 'ml-1.5 opacity-80' : 'ml-1.5 text-emerald-500'}>
              2 months free
            </span>
          </button>
        </div>
      </div>

      {/* `relative` is load-bearing, not decoration. The tick and cross cells each carry an
          `sr-only` label, which is `position: absolute` — and an absolutely positioned
          descendant is only clipped by a scroll container when that container is itself
          positioned. Without it all of them landed at their static positions in the
          document and dragged the whole page wider than a phone. */}
      <div className="border-border/60 bg-card relative mt-10 overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[60rem] text-sm">
          <caption className="sr-only">
            The Techie Gain free trial and plans, compared side by side.
          </caption>
          <thead>
            <tr className="border-border/60 border-b">
              <th
                scope="col"
                className={`text-muted-foreground w-[28%] px-5 pt-6 pb-4 text-left align-bottom text-xs font-semibold tracking-[0.12em] uppercase max-lg:w-[9.5rem] ${PINNED}`}
              >
                Compare plans
              </th>
              <PlanHeading name={TRIAL_PLAN.name} blurb={TRIAL_PLAN.blurb} highlighted={false} />
              {plans.map((plan) => (
                <PlanHeading key={plan.name} name={plan.name} blurb={plan.blurb} highlighted={plan.highlighted} />
              ))}
            </tr>
          </thead>

          <tbody>
            <tr className="border-border/60 border-b">
              <th scope="row" className={`px-5 py-6 text-left align-top font-medium ${PINNED}`}>
                Price
              </th>
              <PriceCell
                amount="$0"
                period={`for ${trialDays} days`}
                note="No card needed"
                cta={TRIAL_PLAN.cta}
                highlighted={false}
              />
              {plans.map((plan) => (
                <PriceCell
                  key={plan.name}
                  amount={`$${priceOf(plan)}`}
                  period={yearly ? '/year' : '/month'}
                  // The monthly equivalent, because "$120 a year" and "$12 a month" are the same
                  // decision and a reader should not have to divide to compare them.
                  note={yearly ? `$${Math.round((priceOf(plan) / 12) * 100) / 100} a month, billed yearly` : ''}
                  cta={plan.cta}
                  highlighted={plan.highlighted}
                />
              ))}
            </tr>

            {COMPARISON.map((group) => (
              <Fragment key={group.group}>
                <tr className="border-border/60 bg-muted/20 border-b">
                  <th
                    scope="colgroup"
                    colSpan={plans.length + 2}
                    className="text-muted-foreground px-5 py-2.5 text-left text-xs font-semibold tracking-[0.12em] uppercase"
                  >
                    {/* The heading spans every column, so it is the text that stays in view, not the cell. */}
                    <span className="sticky left-5 inline-block">{group.group}</span>
                  </th>
                </tr>
                {group.rows.map((row) => {
                  const cells = liveCells(row, live);

                  return (
                    <tr
                      key={row.label}
                      className="border-border/40 hover:bg-muted/20 border-b transition-colors last:border-b-0"
                    >
                      <th scope="row" className={`px-5 py-3 text-left font-normal ${PINNED}`}>
                        {row.label}
                      </th>
                      <ValueCell value={cells.trial} planName={TRIAL_PLAN.name} highlighted={false} />
                      {plans.map((plan, index) => (
                        <ValueCell
                          key={plan.name}
                          value={cells.plans[index] ?? false}
                          planName={plan.name}
                          highlighted={plan.highlighted}
                        />
                      ))}
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PlanHeading({ name, blurb, highlighted }: { name: string; blurb: string; highlighted: boolean }) {
  return (
    <th scope="col" className={`px-5 pt-6 pb-4 text-center align-bottom ${highlighted ? 'bg-primary/5' : ''}`}>
      {/* Reserved on every column, so the headers sit on one baseline whether or not a plan
          carries the badge. */}
      <span className="mb-2 block h-5">
        {highlighted && (
          <span className="bg-primary text-primary-foreground rounded-full px-2.5 py-0.5 text-[10px] font-medium tracking-wide">
            Recommended
          </span>
        )}
      </span>
      <span className="block text-base font-semibold tracking-tight">{name}</span>
      <span className="text-muted-foreground mt-1.5 block text-xs font-normal">{blurb}</span>
    </th>
  );
}

function PriceCell({
  amount,
  period,
  note,
  cta,
  highlighted,
}: {
  amount: string;
  period: string;
  note: string;
  cta: string;
  highlighted: boolean;
}) {
  return (
    <td className={`px-5 py-6 text-center align-top ${highlighted ? 'bg-primary/5' : ''}`}>
      <span className="flex items-baseline justify-center gap-1">
        <span className="text-3xl font-semibold tracking-tight">{amount}</span>
        <span className="text-muted-foreground text-xs">{period}</span>
      </span>
      {/* Height reserved so the buttons stay on one line whatever the note says. */}
      <span className="text-muted-foreground mt-1 block h-4 text-xs">{note}</span>
      <Link
        to="/register"
        className={`mt-4 inline-flex h-10 w-full items-center justify-center rounded-lg px-4 text-sm font-medium transition-opacity hover:opacity-90 ${
          highlighted ? 'bg-primary text-primary-foreground' : 'border-border/60 hover:bg-muted/50 border'
        }`}
      >
        {cta}
      </Link>
    </td>
  );
}

function ValueCell({ value, planName, highlighted }: { value: Cell; planName: string; highlighted: boolean }) {
  return (
    <td className={`px-5 py-3 text-center ${highlighted ? 'bg-primary/5' : ''}`}>
      {typeof value === 'string' ? (
        <span>{value}</span>
      ) : value ? (
        <>
          <Check className="mx-auto size-4 text-emerald-500" aria-hidden />
          <span className="sr-only">Included in {planName}</span>
        </>
      ) : (
        <>
          <X className="text-muted-foreground/40 mx-auto size-4" aria-hidden />
          <span className="sr-only">Not in {planName}</span>
        </>
      )}
    </td>
  );
}
